<script lang="ts">
import { ref, computed, provide, watch, onMounted } from 'vue';

import {
  useBreakpointMediaQueries,
  normalizeResponsiveValue,
  type ResponsiveValue,
} from '@sg-core/resources/assets/js/composables';
import type { BreakpointsWithDefault } from '@sg-core/resources/assets/js/utils';

export interface PhxsgFocusActionPosition {
  x: string;
  y: string;
}

export interface PhxsgFocusAction {
  type: string;
  position?: ResponsiveValue<PhxsgFocusActionPosition>;
  tracking?: Record<string, unknown>;
}

export interface PhxsgFocusProps<T extends PhxsgFocusAction = PhxsgFocusAction> {
  actions: T[];
  a11y?: Record<string, unknown>;
  breakpoints?: BreakpointsWithDefault[];
}

export interface PhxsgFocusEventPayload {
  coordinates?: PhxsgFocusActionPosition;
  tracking?: Record<string, unknown>;
}

export interface PhxsgFocusBackgroundSlotProps {
  active: number | null;
}

export interface PhxsgFocusUspSlotProps<T extends PhxsgFocusAction = PhxsgFocusAction> {
  activeAction: Omit<T, 'position'> | null;
  buttonClass: string;
  isActive: boolean;
  onClick: (event?: Event) => void;
  usp: T;
  position?: PhxsgFocusActionPosition;
}

export interface PhxsgFocusContentSlotProps<T extends PhxsgFocusAction = PhxsgFocusAction> {
  active: number | null;
  activeAction: Omit<T, 'position'> | null;
  viewportSize: BreakpointsWithDefault;
  onActiveReset: () => void;
  onOpenIndex: (index: number, event?: Event) => void;
}
</script>

<script setup lang="ts" generic="T extends PhxsgFocusAction">
const { actions, a11y = {}, breakpoints = [] } = defineProps<PhxsgFocusProps<T>>();

const emit = defineEmits<{
  'focus:usp-open': [payload: PhxsgFocusEventPayload];
  'focus:usp-close': [payload: PhxsgFocusEventPayload];
}>();

const slots = defineSlots<{
  background?(props: PhxsgFocusBackgroundSlotProps): unknown;
  usp?(props: PhxsgFocusUspSlotProps<T>): unknown;
  content(props: PhxsgFocusContentSlotProps<T>): unknown;
}>();

const active = ref<number | null>(null);

const onActionClick = (index: number, event?: Event): void => {
  if (active.value === index) {
    return;
  }

  event?.stopPropagation();
  active.value = index;
};

const activeAction = computed(() => {
  if (active.value === null) {
    return null;
  }

  const { position, ...actionContent } = actions[active.value];

  return actionContent;
});

const viewportSize = useBreakpointMediaQueries(breakpoints);

/**
 * Normalizes a position value (supports both object and array syntax)
 * and returns the position for the current viewport size.
 */
const getPositionForViewport = (
  position: ResponsiveValue<PhxsgFocusActionPosition> | undefined,
): PhxsgFocusActionPosition | undefined => {
  if (!position) {
    return undefined;
  }

  const normalized = normalizeResponsiveValue(position);

  if (typeof normalized === 'object' && normalized !== null && !Array.isArray(normalized)) {
    return (normalized as Record<string, PhxsgFocusActionPosition>)[viewportSize.value];
  }

  return undefined;
};

const getActiveActionData = (index: number): PhxsgFocusEventPayload => {
  return {
    coordinates: getPositionForViewport(actions[index].position),
    ...(actions[index].tracking && {
      tracking: actions[index].tracking,
    }),
  };
};

const onActiveReset = (): void => {
  active.value = null;
};

watch(active, (newValue, oldValue) => {
  if (oldValue !== null) {
    emit('focus:usp-close', getActiveActionData(oldValue));
  }

  if (newValue === null) {
    return;
  }

  emit('focus:usp-open', getActiveActionData(newValue));
});

onMounted(() => {
  if (typeof slots.background === 'undefined') {
    active.value = 0;
  }
});

provide('a11y', a11y);
</script>

<template>
  <div class="phxsg-focus">
    <div class="phxsg-focus__background">
      <slot name="background" :active="active" />

      <template v-for="(action, index) in actions" :key="index">
        <slot
          v-if="action.type === 'usp' && action.position && getPositionForViewport(action.position)"
          name="usp"
          :active-action="activeAction"
          :button-class="'phxsg-focus__button'"
          :is-active="active === index"
          :on-click="(event?: Event) => onActionClick(index, event)"
          :usp="action"
          :position="getPositionForViewport(action.position)"
        />
      </template>
    </div>

    <slot
      name="content"
      :active="active"
      :active-action="activeAction"
      :viewport-size="viewportSize"
      :on-active-reset="onActiveReset"
      :on-open-index="onActionClick"
    />
  </div>
</template>

<style src="./focus.css" />
